import {
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Spinner,
} from "@chakra-ui/react";
import { useCurrentUserQuery } from "features/User/hooks";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { NewHireJourney } from "models/v2/newHireJourney";
import { useReminderRows } from "../hooks";
import SendRemindersQueueModal from "./SendRemindersQueueModal";

interface JourneySendRemindersModalProps {
  journeyId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Self-contained Send Reminders modal — caller only supplies a journey id.
 *
 * Wraps SendRemindersQueueModal and internally fetches the journey, current
 * user, and stakeholder/task data. Queries are gated on `isOpen` so a list
 * view (e.g. the People Team Task Grid) can render a single instance and only
 * pay the data cost when the user actually opens the modal.
 */
export default function JourneySendRemindersModal({
  journeyId,
  isOpen,
  onClose,
}: JourneySendRemindersModalProps) {
  const enabled = isOpen && !!journeyId;

  const { data: journey, isFetched: journeyFetched } = useNewHireJourneyDetailQuery(journeyId, { enabled });
  const { data: currentUser } = useCurrentUserQuery();
  const { rows, youRow, isFetched: rowsFetched } = useReminderRows(journey as NewHireJourney, {
    currentUserId: currentUser?.id,
    enabled: enabled && !!journey,
  });

  if (!isOpen) return null;

  const ready = !!journey && journeyFetched && rowsFetched;
  if (!ready) {
    return (
      <Modal isOpen onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send reminders</ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" justifyContent="center" alignItems="center" py={10}>
            <Spinner />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <SendRemindersQueueModal
      isOpen={isOpen}
      onClose={onClose}
      rows={rows}
      youRow={youRow}
      newHireJourneyId={journey.id}
      newHire={journey.newHire ? {
        fullName: journey.newHire.fullName,
        avatarUrl: journey.newHire.avatarUrl ?? undefined,
        title: journey.newHire.title ?? undefined,
      } : undefined}
    />
  );
}
